// Captures a single still frame of the user's screen/tab via the browser's
// own screen-share API. This gives a true pixel capture — correct for
// blur/translucency/canvas-heavy views (ReactFlow, etc.) that a DOM-to-image
// render (html2canvas) cannot reproduce, which is why this codebase moved
// off that approach. The browser's native "share this tab?" permission
// prompt IS the consent step for this feature — no separate custom consent
// UI needs to be built to satisfy "with consent."
//
// Returns null when the user cancels/denies the browser prompt (a normal,
// expected choice, not an error) or when the API isn't available at all.
// Throws for any other unexpected failure so the caller can show a message.

export interface CapturedScreenshot {
  blob: Blob;
  width: number;
  height: number;
}

export async function captureTabScreenshot(): Promise<CapturedScreenshot | null> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    return null;
  }

  let stream: MediaStream;
  try {
    // Chrome-only hints aren't yet in lib.dom.d.ts — cast the whole
    // constraints object rather than fight the type checker field by field.
    // Ignored harmlessly by other browsers.
    //
    // preferCurrentTab is a TOP-LEVEL option (a sibling of video/audio), not
    // nested inside video — nesting it there (as an earlier version of this
    // file did) silently does nothing, which is why the current tab wasn't
    // appearing/auto-selected in Chrome's picker. Per Chrome's actual API,
    // a valid preferCurrentTab here skips the picker UI entirely and grants
    // the current tab directly — selfBrowserSurface/displaySurface stay
    // inside `video` as documented.
    const constraints = {
      video: {
        displaySurface: "browser",
        selfBrowserSurface: "include",
      },
      audio: false,
      preferCurrentTab: true,
    } as unknown as DisplayMediaStreamOptions;

    stream = await navigator.mediaDevices.getDisplayMedia(constraints);
  } catch (error: any) {
    if (error?.name === "NotAllowedError") {
      return null; // user declined the prompt
    }
    throw error;
  }

  try {
    const track = stream.getVideoTracks()[0];

    let bitmap: ImageBitmap;
    if ("ImageCapture" in window) {
      // @ts-expect-error ImageCapture not in default lib.dom types
      bitmap = await new window.ImageCapture(track).grabFrame();
    } else {
      // Fallback for browsers without ImageCapture: draw one video frame.
      bitmap = await grabFrameViaVideoElement(stream);
    }

    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context unavailable");
    ctx.drawImage(bitmap, 0, 0);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/png")
    );
    if (!blob) throw new Error("Failed to encode screenshot");

    return { blob, width: bitmap.width, height: bitmap.height };
  } finally {
    // Stop immediately — this is a single screenshot, not a recording.
    stream.getTracks().forEach((t) => t.stop());
  }
}

function grabFrameViaVideoElement(stream: MediaStream): Promise<ImageBitmap> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = async () => {
      try {
        await video.play();
        const bitmap = await createImageBitmap(video);
        resolve(bitmap);
      } catch (err) {
        reject(err);
      }
    };
    video.onerror = reject;
  });
}
